import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
// 页面组件导入
import Home from './pages/Home';
import Auth from './pages/Auth';
import PostDetail from './pages/PostDetail';
import Archive from './pages/Archive';
import About from './pages/About';
import WritePost from './pages/WritePost';
import EditPost from './pages/EditPost';
import NotFound from './pages/NotFound';

// 导入全局 Header/Footer
import Header from './components/Header';
import AppFooter from './components/Footer';
import './App.css';

// 仅保留个人信息模板
import { defaultAboutInfoTemplate } from './api/mockData';

// ========== 权限守卫组件（合规：在Router内部使用hooks） ==========
const PrivateRoute = ({ children, loggedInUser }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loggedInUser) {
      navigate('/auth', { 
        replace: true,
        state: { from: location.pathname } 
      });
    }
  }, [loggedInUser, navigate, location]);

  if (!loggedInUser) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        请先登录后访问该页面，正在跳转登录页...
      </div>
    );
  }
  return children;
};

// ========== 核心业务组件（移到Router内部，确保hooks有上下文） ==========
const AppContent = () => {
  // ✅ 关键：hooks现在在Router内部调用，合规！
  const navigate = useNavigate();
  const location = useLocation();

  // 核心状态管理
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aboutInfo, setAboutInfo] = useState(defaultAboutInfoTemplate);
  const [aboutLoading, setAboutLoading] = useState(false);

  // 统一存储key
  const POSTS_STORAGE_KEY = 'myBlogPosts';
  const USER_STORAGE_KEY = 'blog_currentUser';

  // 退出登录方法
  const handleLogout = useCallback(() => {
    message.destroy();
    setLoggedInUser(null);
    try {
      sessionStorage.removeItem(USER_STORAGE_KEY);
    } catch (e) {
      console.warn('清除登录信息失败', e);
    }
    navigate('/auth', { replace: true });
    message.success('已安全退出登录', 1.5);
  }, [navigate]);

  // 初始化文章数据
  useEffect(() => {
    const initPostsData = () => { 
      try {
        const savedPosts = localStorage.getItem(POSTS_STORAGE_KEY);
        let initPosts = [];
        
        if (savedPosts) {
          try {
            initPosts = JSON.parse(savedPosts);
            initPosts = Array.isArray(initPosts) ? initPosts : [];
          } catch (e) {
            console.warn('本地存储解析失败，使用空数组');
            initPosts = [];
          }
        }
        
        const normalizedPosts = initPosts.map(post => ({
          id: post?.id || `post_${Math.floor(Math.random() * 10000)}`,
          authorId: post?.authorId || '0',
          title: post?.title || '未命名文章',
          content: post?.content || '',
          date: post?.date || new Date().toISOString().split('T')[0],
          createTime: post?.createTime || new Date().toLocaleString(),
          summary: post?.summary || post?.content?.slice(0, 50)?.replace(/\n/g, ' ') + '...' || '暂无简介',
          tags: Array.isArray(post?.tags) ? post.tags.filter(Boolean) : [],
          likeCount: post?.likeCount || 0,
          commentCount: post?.commentCount || post?.comments?.length || 0,
          comments: post?.comments || []
        }));
        
        setPosts(normalizedPosts);
      } catch (err) {
        setError(err);
        message.destroy();
        message.error('文章数据加载失败，请刷新重试', 2);
      } finally {
        setLoading(false);
      }
    };

    const delay = process.env.NODE_ENV === 'development' ? 800 : 0;
    const timer = setTimeout(initPostsData, delay);
    return () => clearTimeout(timer);
  }, []);

  // 初始化登录状态
  useEffect(() => {
    const userStr = sessionStorage.getItem(USER_STORAGE_KEY);
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.id && user?.username) {
          setLoggedInUser(user);
        } else {
          sessionStorage.removeItem(USER_STORAGE_KEY);
          setLoggedInUser(null);
        }
      } catch (e) {
        sessionStorage.removeItem(USER_STORAGE_KEY);
        setLoggedInUser(null);
      }
    }
  }, []);

  // 登录/注册逻辑
  const handleLogin = useCallback((values) => {
    message.destroy();
    if (!values?.username) {
      message.warning('用户名不能为空！');
      return;
    }
    const user = {
      id: String(Date.now()),
      username: values.username,
      nickname: values.username || '博客用户'
    };
    setLoggedInUser(user);
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    message.success(`欢迎回来，${values.username}！`, 1.5);
  }, []);

  const handleRegister = useCallback((values) => {
    message.destroy();
    if (!values?.username) {
      message.warning('用户名不能为空！');
      return;
    }
    if (!values?.password || values.password.length < 6) {
      message.warning('密码长度不能少于6位！');
      return;
    }
    const user = {
      id: String(Date.now()),
      username: values.username,
      nickname: values.username,
      email: values.email || ''
    };
    setLoggedInUser(user);
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    message.success(`注册成功！欢迎 ${values.username}~`, 1.5);
  }, []);

  // 删除文章逻辑
  const handleDeletePost = useCallback((postId) => {
    message.destroy();
    
    if (!loggedInUser) {
      message.warning('请先登录后操作', 1.5);
      navigate('/auth');
      return;
    }

    if (!postId) {
      message.error('无效的文章ID！', 1.5);
      return;
    }
    const targetPostId = String(postId).trim();
    const targetPost = posts.find(post => String(post.id) === targetPostId);
    
    if (!targetPost) {
      message.error('文章不存在！', 1.5);
      return;
    }
    if (String(targetPost.authorId) !== String(loggedInUser.id)) {
      message.error('你没有权限删除该文章！', 1.5);
      return;
    }

    const newPosts = posts.filter(post => String(post.id) !== targetPostId);
    setPosts(newPosts);
    localStorage.setItem(POSTS_STORAGE_KEY, JSON.stringify(newPosts));
    message.success('文章删除成功！', 1.5);
    navigate('/home');
  }, [loggedInUser, posts, navigate]);

  // 发布文章逻辑
  const handleAddPost = useCallback((newPost) => {
    message.destroy();
    if (!loggedInUser) {
      message.warning('请先登录后发布文章', 1.5);
      navigate('/auth');
      return;
    }
    if (!newPost?.title || !newPost?.content) {
      message.warning('标题和内容不能为空！', 1.5);
      return;
    }

    const newPostId = `post_${Date.now()}`;
    const postWithMeta = {
      ...newPost,
      id: newPostId,
      authorId: String(loggedInUser.id),
      date: new Date().toISOString().split('T')[0],
      createTime: new Date().toLocaleString(),
      likeCount: 0,
      commentCount: 0,
      comments: []
    };

    const newPosts = [postWithMeta, ...posts];
    setPosts(newPosts);
    localStorage.setItem(POSTS_STORAGE_KEY, JSON.stringify(newPosts));
    message.success('文章发布成功！', 1.5);
  }, [loggedInUser, posts, navigate]);

  // 编辑文章逻辑
  const handleEditPost = useCallback((postId, updatedPost) => {
    message.destroy();
    if (!loggedInUser) {
      message.warning('请先登录后操作', 1.5);
      navigate('/auth');
      return;
    }
    if (!updatedPost?.title || !updatedPost?.content) {
      message.warning('标题和内容不能为空！', 1.5);
      return;
    }
    if (!postId) {
      message.error('无效的文章ID！', 1.5);
      return;
    }

    const targetPostId = String(postId).trim();
    const targetPostIndex = posts.findIndex(post => String(post.id) === targetPostId);
    
    if (targetPostIndex === -1) {
      message.error('文章不存在！', 1.5);
      return;
    }
    const targetPost = posts[targetPostIndex];
    if (String(targetPost.authorId) !== String(loggedInUser.id)) {
      message.error('你没有权限编辑该文章！', 1.5);
      return;
    }

    const updatedPostWithMeta = {
      ...targetPost,
      ...updatedPost,
      tags: Array.isArray(updatedPost.tags) ? updatedPost.tags.filter(Boolean) : [],
      summary: updatedPost.summary || updatedPost.content.slice(0, 50) + '...'
    };

    const newPosts = [...posts];
    newPosts[targetPostIndex] = updatedPostWithMeta;
    setPosts(newPosts);
    localStorage.setItem(POSTS_STORAGE_KEY, JSON.stringify(newPosts));
    message.success('文章编辑成功！', 1.5);
  }, [loggedInUser, posts, navigate]);

  // 获取单篇文章
  const getPostById = useCallback((postId) => {
    if (!postId) return null;
    const targetPostId = String(postId).trim();
    return posts.find(post => String(post.id) === targetPostId) || null;
  }, [posts]);

  // 全局登录态兜底校验
  useEffect(() => {
    if (!loggedInUser && location.pathname !== '/auth' && !location.pathname.startsWith('/auth')) {
      navigate('/auth', { replace: true });
    }
  }, [loggedInUser, navigate, location]);

  // 路由渲染
  return (
    <>
      {/* Header：登录后显示 */}
      {loggedInUser && <Header 
        loggedInUser={loggedInUser} 
        onLogout={handleLogout} 
      />}

      {/* 内容区域 */}
      <div 
        className="app-content" 
        style={{ 
          minHeight: loggedInUser ? 'calc(100vh - 120px)' : '100vh', 
          padding: loggedInUser ? '24px 16px 80px' : '0',
          boxSizing: 'border-box'
        }}
      >
        <Routes>
          {/* 默认路由跳登录页 */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          
          {/* 登录/注册页 */}
          <Route
            path="/auth"
            element={
              loggedInUser ? (
                <>
                  <div style={{ padding: '50px', textAlign: 'center' }}>
                    你已登录，正在跳转到首页...
                  </div>
                  <Navigate to="/home" replace state={{ autoLogin: true }} />
                </>
              ) : (
                <Auth
                  onLogin={handleLogin}
                  onRegister={handleRegister}
                />
              )
            }
          />

          {/* 首页 */}
          <Route
            path="/home"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <Home
                  posts={posts}
                  loading={loading}
                  error={error}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                  onDeletePost={handleDeletePost}
                  onEditPost={(postId) => `/edit/${postId}`}
                />
              </PrivateRoute>
            }
          />

          {/* 文章详情页 */}
          <Route
            path="/post/:id"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <PostDetail
                  posts={posts}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                  getPostById={getPostById}
                  onDeletePost={handleDeletePost}
                />
              </PrivateRoute>
            }
          />

          {/* 归档页 */}
          <Route
            path="/archive"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <Archive
                  posts={posts}
                  loading={loading}
                  error={error}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                  onDeletePost={handleDeletePost}
                  onEditPost={handleEditPost}
                  getPostById={getPostById}
                />
              </PrivateRoute>
            }
          />

          {/* 关于页 */}
          <Route
            path="/about"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <About
                  aboutInfo={aboutInfo}
                  onUpdateAboutInfo={(newInfo) => {
                    if (!newInfo || typeof newInfo !== 'object') return;
                    setAboutLoading(true);
                    setTimeout(() => {
                      setAboutInfo({ ...aboutInfo, ...newInfo });
                      setAboutLoading(false);
                      message.success('个人信息更新成功！', 1.5);
                    }, 600);
                  }}
                  loading={aboutLoading}
                  canEdit={!!loggedInUser}
                  onLogout={handleLogout}
                  loggedInUser={loggedInUser}
                />
              </PrivateRoute>
            }
          />

          {/* 写文章页 */}
          <Route
            path="/write"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <WritePost
                  onAddPost={handleAddPost}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                />
              </PrivateRoute>
            }
          />

          {/* 编辑文章页 */}
          <Route
            path="/edit/:id"
            element={
              <PrivateRoute loggedInUser={loggedInUser}>
                <EditPost
                  getPostById={getPostById}
                  onEditPost={handleEditPost}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                />
              </PrivateRoute>
            }
          />

          {/* 404页面 */}
          <Route
            path="*"
            element={
              <NotFound 
                loggedInUser={loggedInUser} 
                onLogout={handleLogout} 
              />
            }
          />
        </Routes>
      </div>

      {/* Footer：登录后显示 */}
      {loggedInUser && <AppFooter />}
    </>
  );
};

// ========== 根App组件（仅包裹Router） ==========
function App() {
  return (
    <Router>
      <AppContent /> {/* 核心逻辑移到Router内部 */}
    </Router>
  );
}

export default App;